/* eslint-disable no-restricted-syntax */
import isEqual from 'lodash/isEqual';
import moment from 'moment';

/** Relative time for a record's own clock fields, clamped so a server timestamp
 *  running ahead of the browser never renders as "in a few seconds". */
export function convertUnixTimeToMoment(timestamp) {
  const now = moment();
  const then = moment(new Date(timestamp * 1000));
  return (then.isAfter(now) ? now : then).fromNow();
}

export function convertUnixTimeToPublishedMoment(timestamp, dateOnly) {
  const date = new Date(timestamp * 1000);
  if (dateOnly) return moment.utc(date).fromNow();
  return moment(date).fromNow();
}

const USE_ORIGINAL_IMAGES = process.env.REACT_APP_USE_ORIGINAL_IMAGES === 'true';

function getAuthorImageCachedUrl(authorInfo, scope) {
  if (USE_ORIGINAL_IMAGES) return null;
  if (authorInfo && authorInfo.imageCached && authorInfo.id && scope) {
    return `${process.env.REACT_APP_API_URL}/images/authors/${scope}/${authorInfo.id}/medium`;
  }
  return null;
}

export function getAuthorImageUrl(authorInfo, scope) {
  const cachedUrl = getAuthorImageCachedUrl(authorInfo, scope);
  if (cachedUrl) return cachedUrl;

  if (!authorInfo || !authorInfo.profiles || !authorInfo.profiles.length) {
    return null;
  }

  for (let i = 0; i < authorInfo.profiles.length; i += 1) {
    const profile = authorInfo.profiles[i];
    if (profile.imageUrl) return profile.imageUrl;
  }

  return null;
}

function resolveAuthorImageScope(author) {
  if (author?.privateData?.imageCached) return author.imageScope;
  if (author?.publicData?.imageCached) return 'public';
  return null;
}

export function getSiteImageUrl(site) {
  return site && (site.logo || `https://img.logo.dev/${site.domain}?token=pk_Rtf-kQfRRIKjZ_vMaqiduA&size=800`);
}

function getCoverImageCachedUrl(item) {
  if (USE_ORIGINAL_IMAGES) return null;
  if (item.coverImageCached && item.coverImageScope) {
    return `${process.env.REACT_APP_API_URL}/images/covers/${item.coverImageScope}/${item.id}/medium`;
  }
  return null;
}

export function getListCoverImageUrl(listInfo) {
  if (!USE_ORIGINAL_IMAGES && listInfo.coverImageCached && listInfo.coverImageScope) {
    return `${process.env.REACT_APP_API_URL}/images/lists/${listInfo.coverImageScope}/${listInfo.id}/medium`;
  }
  return listInfo.coverImageUrl || null;
}

export function getSiteUrl(site) {
  if (!site) {
    return null;
  }

  return `https://${site.domain}`;
}

export function getPrimaryLink(item) {
  if (!item || !item.links || !item.links.length) return null;

  const primaryLink = item.links.find((link) => link.primary === true);
  return primaryLink || null;
}

export function getPrimaryLinkUrl(item) {
  const primaryLink = getPrimaryLink(item);
  return primaryLink ? primaryLink.url : null;
}

export function openItemUrlInNewTab(item) {
  const primaryLinkUrl = getPrimaryLinkUrl(item);
  if (primaryLinkUrl) {
    window.open(primaryLinkUrl, '_blank');
  }
}

// The ten fields a complete item has, each worth ten points, paired with the
// name the editor calls it by.
const COMPLETENESS_FIELDS = [
  ['title', 'title'],
  ['link', 'link'],
  ['authors', 'author'],
  ['coverImageUrl', 'cover image'],
  ['excerpt', 'excerpt'],
  ['type', 'type'],
  ['lang', 'language'],
  ['timeNeeded', 'read time'],
  ['timePublished', 'publish date'],
  ['tags', 'tags'],
];

export function scoreCompleteness(filled) {
  const missing = COMPLETENESS_FIELDS
    .filter(([key]) => !filled[key])
    .map(([, label]) => label);
  return { score: (COMPLETENESS_FIELDS.length - missing.length) * 10, missing };
}

export function getItemCompleteness(item) {
  return scoreCompleteness({
    title: Boolean(item.title),
    link: Boolean(getPrimaryLink(item)),
    authors: (item.authors || []).length > 0,
    coverImageUrl: Boolean(item.coverImageUrl),
    excerpt: Boolean(item.excerpt),
    type: Boolean(item.type),
    lang: Boolean(item.lang),
    timeNeeded: Boolean(item.timeNeeded),
    timePublished: Boolean(item.timePublished),
    tags: (item.tags || []).length > 0,
  });
}

export function humanizeIntervalInMinutes(minutes) {
  return moment.duration(minutes, 'minutes').humanize();
}

// Rescales raw heat/buzz to [0,100] for display.
function scaleHeatBuzz(heatNorm, buzzNorm) {
  return {
    heatScore: heatNorm != null ? Math.max(0, Math.min(100, heatNorm * 100)) : null,
    buzzScore: buzzNorm != null ? Math.max(0, Math.min(100, (buzzNorm * 100) / 7)) : null,
  };
}

// Decodes heat/buzz from extra.heatSort/buzzSort. Stripping the publish/create-time
// tiebreaker and dividing by the 1e14 bucket scale gives the raw values
// (`heatNorm`, `buzzNorm`).
export function getHeatBuzz(item) {
  const tiebreak = item.timePublished || item.timeCreated || 0;
  const heatSort = item.extra?.heatSort;
  const buzzSort = item.extra?.buzzSort;
  const heatNorm = heatSort != null ? (heatSort - tiebreak) / 1e14 : null;
  const buzzNorm = buzzSort != null ? (buzzSort - tiebreak) / 1e14 : null;
  return { ...scaleHeatBuzz(heatNorm, buzzNorm), heatNorm, buzzNorm };
}

// An author or site stores its raw heat/buzz in `extra`, with no sort key to decode.
export function getEntityHeatBuzz(entity) {
  const { heat, buzz } = entity.extra || {};
  return scaleHeatBuzz(heat ?? null, buzz ?? null);
}

export function getDomainFromUrl(url) {
  const parser = document.createElement('a');
  parser.href = url;
  return parser.hostname;
}

export function getDomain(item) {
  const primaryLink = getPrimaryLink(item);
  return primaryLink && primaryLink.url ? getDomainFromUrl(primaryLink.url) : '';
}

export function getSourceLetter(item) {
  const domain = getDomain(item);
  if (domain) {
    const arr = domain.split('.');
    if (arr[0] === 'www' || arr[0] === 'm') {
      return arr[1][0].toUpperCase();
    }
    return arr[0][0].toUpperCase();
  } if (item.title) {
    return item.title[0].toUpperCase();
  }
  return '-';
}

export function getAuthorInfoName(authorInfo) {
  if (!authorInfo || !authorInfo.profiles || !authorInfo.profiles.length) {
    return null;
  }

  let username = null;
  for (let i = 0; i < authorInfo.profiles.length; i += 1) {
    const profile = authorInfo.profiles[i];
    if (profile.name) {
      return profile.name;
    }

    if (!username && profile.username) {
      username = profile.username;
    }
  }

  return username ? `@${username}` : null;
}

export function getSiteTitle(site) {
  if (!site) return null;

  // Check private data first
  if (site.privateData) {
    const title = site.privateData.title || site.privateData.domain;
    if (title) return title;
  }

  // Then check public data
  if (site.publicData) {
    return site.publicData.title || site.publicData.domain;
  }

  // Finally check direct properties (for merged/flattened site objects)
  return site.title || site.domain || null;
}

export function getAuthorName(author) {
  if (!author) return null;

  let name;
  if (author.privateData) {
    name = getAuthorInfoName(author.privateData);
    if (name) return name;
  }

  if (author.publicData) {
    return getAuthorInfoName(author.publicData);
  }

  return null;
}

// Deterministic avatar palette: an author's name hashes to a stable color, so
// the same author always gets the same color.
export const AUTHOR_COLORS = [
  '#34506b', '#455a64', '#5d4037', '#37474f',
  '#4e342e', '#3e4a5b', '#4b5e52', '#5a4a63',
];

export function authorColor(name) {
  const s = name || '';
  let h = 0;
  // eslint-disable-next-line no-bitwise -- >>> 0 coerces the running hash to uint32
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AUTHOR_COLORS[h % AUTHOR_COLORS.length];
}

// Two-letter monogram for an author without a picture. Single-word names take
// their first two letters; multi-word names take the first and last initials.
export function authorInitials(name) {
  const parts = (name || '').replace(/[.,]/g, '').split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function resolveCoverImageScope(item) {
  if (item.privateData?.coverImageCached) return item.imageScope;
  if (item.publicData?.coverImageCached) return 'public';
  return null;
}

export function mergePublicAndPrivateItems(item) {
  if (!item || !(item.privateData || item.publicData)) {
    return null;
  }

  const coverImageScope = resolveCoverImageScope(item);

  if (!item.publicData) {
    return { ...item.privateData, hasPrivateData: true, ...(item.extra && { extra: item.extra }), ...(coverImageScope && { coverImageScope }) };
  }

  if (!item.privateData) {
    return { ...item.publicData, hasPublicData: true, ...(item.extra && { extra: item.extra }), ...(coverImageScope && { coverImageScope }) };
  }

  const merged = { ...item.publicData, ...item.privateData, hasPrivateData: true, hasPublicData: true };

  // Merge authors from both private and public data
  if (item.publicData.authors || item.privateData.authors) {
    const authorsMap = new Map();
    if (item.publicData.authors) {
      item.publicData.authors.forEach((author) => authorsMap.set(author.id, author));
    }
    if (item.privateData.authors) {
      item.privateData.authors.forEach((author) => authorsMap.set(author.id, author));
    }
    merged.authors = Array.from(authorsMap.values());
  }

  // Include extra data from top-level item
  if (item.extra) {
    merged.extra = item.extra;
  }

  if (coverImageScope) {
    merged.coverImageScope = coverImageScope;
  }

  return merged;
}

// Mirrors server side list of compared fields.
const SHARE_COMPARED_FIELDS = [
  'title', 'excerpt', 'lang', 'type', 'coverImageUrl', 'timeNeeded', 'timePublished',
];

const SHARE_FIELD_LABELS = {
  title: 'title',
  excerpt: 'excerpt',
  lang: 'language',
  type: 'type',
  coverImageUrl: 'cover image',
  timeNeeded: 'reading time',
  timePublished: 'publish date',
  tagIds: 'tags',
  authorIds: 'authors',
  links: 'links',
  profiles: 'profiles',
  domain: 'domain',
  logo: 'logo',
  description: 'description',
};

// An absent field and an empty one are equal here.
function shareValue(record, field) {
  return record?.[field] || undefined;
}

// Ids are aligned across namespaces; their order carries no meaning.
function normalizeShareIds(ids) {
  return ids?.length ? [...ids].sort() : [];
}

// Only these four link fields are proposable - the rest are derived from the url.
function normalizeShareLinks(links) {
  if (!links?.length) return [];
  return links
    .map((link) => JSON.stringify([
      link.url || '', link.primary === true, link.visible === true, link.siteId || '',
    ]))
    .sort();
}

/**
 * Names the fields proposed by sharing this item, empty when it would add
 * nothing. Mirrors server-side diff, which is the authority.
 */
export function diffItemAgainstPublic(item, resolvedTagIds) {
  const mine = item?.privateData;
  if (!mine) return [];
  const theirs = item.publicData || {};

  const changed = SHARE_COMPARED_FIELDS.filter(
    (field) => shareValue(mine, field) !== shareValue(theirs, field),
  );

  if (!isEqual(normalizeShareIds(resolvedTagIds || mine.tagIds), normalizeShareIds(theirs.tagIds))) {
    changed.push('tagIds');
  }
  // Author order matters (it's the byline).
  if (!isEqual(mine.authorIds || [], theirs.authorIds || [])) {
    changed.push('authorIds');
  }
  if (!isEqual(normalizeShareLinks(mine.links), normalizeShareLinks(theirs.links))) {
    changed.push('links');
  }

  return changed;
}

// The account a profile points to, ignoring the mutable display fields
// (imageUrl, isPrimary). Mirrors the server's profile identity.
function profileIdentity(profile) {
  if (!profile) return null;
  const type = profile.type ?? '';
  const handle = profile.username || profile.id;
  if (handle) return `h:${type}:${String(handle).toLowerCase()}`;
  if (profile.url) return `u:${profile.url.toLowerCase()}`;
  if (profile.name) return `n:${type}:${profile.name.toLowerCase()}`;
  return null;
}

function isSameProfile(a, b) {
  const idA = profileIdentity(a);
  const idB = profileIdentity(b);
  if (idA !== null && idB !== null) return idA === idB;
  return isEqual(a, b);
}

/**
 * Names the fields proposed by sharing this author, empty when it would add
 * nothing. Mirrors server-side diff: a share adds or restates a profile, so a
 * profile dropped by the user proposes nothing.
 */
export function diffAuthorAgainstPublic(author) {
  const mine = author?.privateData;
  if (!mine) return [];
  const publicProfiles = author.publicData?.profiles || [];

  const proposes = (mine.profiles || []).some((profile) => {
    const counterpart = publicProfiles.find((p) => isSameProfile(p, profile));
    return !counterpart || !isEqual(counterpart, profile);
  });

  return proposes ? ['profiles'] : [];
}

const SHARE_COMPARED_SITE_FIELDS = ['title', 'domain', 'logo'];

// Mirrors the server's normalizeHostname.
function normalizeHostname(hostname) {
  return (hostname || '').toLowerCase().replace(/^www\./, '');
}

export function isSameSiteDomain(domainA, domainB) {
  return normalizeHostname(domainA) === normalizeHostname(domainB);
}

/**
 * Names the fields proposed by sharing this site, empty when it would add
 * nothing. Mirrors server-side diff: a share adds or restates a field, so a
 * field dropped by the user proposes nothing.
 */
export function diffSiteAgainstPublic(site) {
  const mine = site?.privateData;
  if (!mine) return [];
  const theirs = site.publicData || {};

  return SHARE_COMPARED_SITE_FIELDS.filter((field) => {
    const value = shareValue(mine, field);
    if (!value) return false;
    if (field === 'domain') return !isSameSiteDomain(value, theirs.domain);
    return value !== shareValue(theirs, field);
  });
}

const SHARE_COMPARED_LIST_FIELDS = ['title', 'description', 'coverImageUrl'];

/**
 * Names the fields proposed by sharing this list, empty when it would add
 * nothing. Mirrors server-side diff: a share adds or restates a field, so a
 * field dropped by the user proposes nothing. All tags are treated as one field,
 * dropping a tag makes a difference.
 */
export function diffListAgainstPublic(list, resolvedTagIds) {
  const mine = list?.privateData;
  if (!mine) return [];
  const theirs = list.publicData || {};

  const changed = SHARE_COMPARED_LIST_FIELDS.filter(
    (field) => shareValue(mine, field) && shareValue(mine, field) !== shareValue(theirs, field),
  );

  if (!isEqual(normalizeShareIds(resolvedTagIds || mine.tagIds), normalizeShareIds(theirs.tagIds))) {
    changed.push('tagIds');
  }

  return changed;
}

// Reads a share diff back as a tooltip fragment: "title, tags".
export function describeShareDiff(fields) {
  if (!fields || !fields.length) return '';
  return fields.map((field) => SHARE_FIELD_LABELS[field] || field).join(', ');
}

/**
 * What the share button has to offer: 'unknown' when nobody computed a diff,
 * which reads as "might have something" rather than as nothing left to add.
 */
export function shareChangesState(shareDiff) {
  if (!shareDiff) return 'unknown';
  return shareDiff.length ? 'some' : 'none';
}

/**
 * What to tell the user a share did.
 */
export function shareResultMessage(result) {
  return result?.shared === false
    ? 'Nothing to share — this matches the public copy.'
    : 'Shared.';
}

// The share button's tooltip including the fields proposed by sharing this item.
export function shareActionTitle(hasPublicData, shareDiff) {
  if (!hasPublicData) return 'Share';
  const adds = describeShareDiff(shareDiff);
  if (adds) return `Share changes: ${adds}`;
  return shareChangesState(shareDiff) === 'none'
    ? 'Shared — no changes to share'
    : 'Share changes';
}

function mergePublicAndPrivate(entity) {
  if (!entity || !(entity.privateData || entity.publicData)) {
    return null;
  }
  if (!entity.publicData) {
    return { ...entity.privateData, hasPrivateData: true };
  }

  if (!entity.privateData) {
    return { ...entity.publicData, hasPublicData: true };
  }

  return {
    ...entity.publicData, ...entity.privateData, hasPrivateData: true, hasPublicData: true,
  };
}

export function mergeAuthor(author) {
  const merged = mergePublicAndPrivate(author);
  if (!merged) return null;
  const authorImageScope = resolveAuthorImageScope(author);
  if (authorImageScope) merged.authorImageScope = authorImageScope;
  return merged;
}

export function mergeSite(site) {
  return mergePublicAndPrivate(site);
}

export function isPublicNamespace(namespace) {
  return namespace === 'public';
}

export function mustSignIn(namespace, page, userInfo) {
  const isPublic = isPublicNamespace(namespace);
  if (isPublic && !userInfo && page > 1) {
    return true;
  }

  if (!isPublic && !userInfo) {
    return true;
  }

  return false;
}

function populateItemTagInfo(itemInfo, tagInfoMap) {
  if (!itemInfo || !itemInfo.tagIds || !tagInfoMap) return;
  const { tagIds } = itemInfo;
  const tags = [];
  for (const tagId of tagIds) {
    const tagInfo = tagInfoMap[tagId];
    if (tagInfo) tags.push(tagInfo);
  }
  // eslint-disable-next-line no-param-reassign
  if (tags.length) itemInfo.tags = tags;
}

export function populateItemTags(items, privateTagInfoMap, publicTagInfoMap) {
  if (!items || !items.length) return;
  for (const item of items) {
    populateItemTagInfo(item.privateData, privateTagInfoMap);
    populateItemTagInfo(item.publicData, publicTagInfoMap);
  }
}

function populateItemAuthorInfo(itemInfo, authorInfoMap) {
  if (!itemInfo || !itemInfo.authorIds || !authorInfoMap) return;
  const { authorIds } = itemInfo;
  const authors = [];
  for (const authorId of authorIds) {
    const author = authorInfoMap[authorId];
    if (author) authors.push(author);
  }
  // eslint-disable-next-line no-param-reassign
  if (authors.length) itemInfo.authors = authors;
}

export function populateItemAuthors(items, authorInfoMap) {
  if (!items || !items.length || !authorInfoMap) return;
  for (const item of items) {
    populateItemAuthorInfo(item.privateData, authorInfoMap);
    populateItemAuthorInfo(item.publicData, authorInfoMap);
  }
}

function populateItemUrlSites(item, sites) {
  if (!item || !item.links || !sites) return;
  for (const link of item.links) {
    if (link && link.siteId && sites[link.siteId]) link.site = sites[link.siteId];
  }
}

export function populateItemSites(itemsCombined, sites) {
  if (!itemsCombined || !itemsCombined.length || !sites) return;
  for (const item of itemsCombined) {
    populateItemUrlSites(item.privateData, sites);
    populateItemUrlSites(item.publicData, sites);
  }
}

export function populateItemsResponse(items, response) {
  const { authors, tags, sites } = response || {};
  if (authors) populateItemAuthors(items, authors);
  if (tags) populateItemTags(items, tags.privateData, tags.publicData);
  if (sites) populateItemSites(items, sites);
}

function populateItemListInfo(itemInfo, lists) {
  if (!itemInfo || !itemInfo.lists || !lists) return;
  const itemLists = [];
  for (const listId of itemInfo.lists) {
    const list = lists[listId];
    if (list) itemLists.push(list);
  }
  // eslint-disable-next-line no-param-reassign
  if (itemLists.length) itemInfo.listInfos = itemLists;
}

export function populateItemLists(items, lists) {
  if (!items || !items.length || !lists) return;
  for (const item of items) {
    populateItemListInfo(item.privateData, lists);
    populateItemListInfo(item.publicData, lists);
  }
}

export function getLogoUrl(item) {
  const link = getPrimaryLink(item);
  if (link && link.site) {
    const combined = mergeSite(link.site);
    return combined && getSiteImageUrl(combined);
  }

  return null;
}

export function getImageInfo(item) {
  const storedUrl = getCoverImageCachedUrl(item);
  if (storedUrl) return { url: storedUrl, source: 'cover' };

  if ('coverImageUrl' in item) {
    return { url: item.coverImageUrl, source: 'cover' };
  }

  if (item.authors) {
    for (const author of item.authors) {
      let imageUrl = getAuthorImageUrl(author.privateData, author.imageScope);
      if (imageUrl) return { url: imageUrl, source: 'author' };
      imageUrl = getAuthorImageUrl(author.publicData, 'public');
      if (imageUrl) return { url: imageUrl, source: 'author' };
    }
  }

  const logoUrl = getLogoUrl(item);
  if (logoUrl) return { url: logoUrl, source: 'logo' };

  return { url: null, source: null };
}

export function getPrimarySiteId(item) {
  const link = getPrimaryLink(item);
  return link && link.siteId;
}

export function getPrimarySite(item) {
  const link = getPrimaryLink(item);
  return (link && link.site) || null;
}
